import { ReactNode, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';

import { iframeAuthService } from 'src/api/iframeAuth';
import { Modality } from 'src/types/api/data/image';
import localStore from 'src/utils/localStore';

const Container = styled('div')`
  width: 100%;
  height: 100vh;
  box-sizing: border-box;
  overflow-y: auto;
`;

const CenteredContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

interface ProjectPageContainerProps {
  children: (projectId: string, modalityLabel: Modality) => ReactNode;
}

export default function ProjectPageContainer({
  children,
}: ProjectPageContainerProps): JSX.Element {
  const [isInitializing, setIsInitializing] = useState(true);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [modalityLabel, setModalityLabel] = useState<Modality>(Modality.DBT);

  useEffect(() => {
    const initialize = async () => {
      // If in iframe, wait for authentication to complete
      if (iframeAuthService.isInIframe()) {
        await iframeAuthService.waitForInitialization();
      }

      // Get project ID from localStorage
      const storedProjectId = localStore.getAuthzTargetProjectId();
      setProjectId(storedProjectId);

      // Parse modality from authz_project_metadata
      let modality: Modality = Modality.DBT; // Default fallback
      const metadata = localStore.getAuthzMetadata();

      if (metadata) {
        try {
          const metadataObj = JSON.parse(metadata);
          if (metadataObj.modality) {
            modality = metadataObj.modality as Modality;
          }
        } catch (error) {
          console.error(
            '[ProjectPageContainer] Failed to parse metadata:',
            error
          );
        }
      }

      setModalityLabel(modality);
      setIsInitializing(false);
    };

    initialize();
  }, []);

  // Show loading spinner during initialization
  if (isInitializing) {
    return (
      <CenteredContainer>
        <CircularProgress />
      </CenteredContainer>
    );
  }

  // After initialization, if still no project ID, show error
  if (!projectId) {
    return (
      <Container>
        <div>
          Project ID not found in localStorage. Please make sure you have
          authenticated properly.
        </div>
      </Container>
    );
  }

  return <Container>{children(projectId, modalityLabel)}</Container>;
}
